import React, { useEffect, useState, useRef, useCallback } from "react";
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../context/LanguageContext";
import { formatLanguageName } from "../utils/languageUtils";

const LanguageNotification: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const [showNotification, setShowNotification] = useState(false);
  const [previousLanguage, setPreviousLanguage] = useState(currentLanguage);
  const [isClosing, setIsClosing] = useState(false);
  const [timeLeft, setTimeLeft] = useState(3);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const countdownValueRef = useRef(3);

  const startCountdown = useCallback(() => {
    // Clear any existing timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    countdownValueRef.current = 3;
    setTimeLeft(3);
    
    countdownRef.current = setInterval(() => {
      countdownValueRef.current -= 1;
      setTimeLeft(countdownValueRef.current);
      
      if (countdownValueRef.current <= 0) {
        setIsClosing(true);
        if (countdownRef.current) clearInterval(countdownRef.current);
        setTimeout(() => setShowNotification(false), 300); // Wait for exit animation
      }
    }, 1000);
  }, []);

  useEffect(() => {
    if (currentLanguage !== previousLanguage) {
      setShowNotification(true);
      setIsClosing(false);
      setPreviousLanguage(currentLanguage);
      
      // Start countdown after a small delay to ensure state is set
      setTimeout(() => {
        startCountdown();
      }, 100);
    }
  }, [currentLanguage, previousLanguage, startCountdown]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    setTimeout(() => setShowNotification(false), 300); // Wait for exit animation
  }, []);

  if (!showNotification) {
    return null;
  }

  return (
    <div className={`language-notification ${isClosing ? "closing" : ""}`}>
      <div className="flex items-center space-x-2">
        <CheckCircleIcon className="w-5 h-5 text-white" />
        <span className="text-sm font-medium">
          Language changed to {formatLanguageName(currentLanguage, false)}
        </span>
        <div className="flex items-center space-x-2 ml-2">
          {/* Progress bar */}
          <div className="flex items-center space-x-2">
            <div className="w-16 h-1 bg-white/30 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all duration-1000 ease-linear"
                style={{ width: `${(timeLeft / 3) * 100}%` }}
              ></div>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
            title="Close notification"
            aria-label="Close language change notification"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageNotification;
